import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Clock, Award, BookOpen, Calendar } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/layout/Navbar';
import Header from '@/components/layout/Header';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { fetchProfileStats } from '@/services/stats';
import { toast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';

const Profile: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [completedCourses, setCompletedCourses] = useState(0);
  const [inProgressCourses, setInProgressCourses] = useState(0);
  const [totalLessonsCompleted, setTotalLessonsCompleted] = useState(0);
  const [totalCourses, setTotalCourses] = useState(0);
  const [lastActivity, setLastActivity] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Use the auth redirect hook
  useAuthRedirect(false);
  
  // Fetch profile stats effect
  useEffect(() => {
    const loadProfileStats = async () => {
      setIsLoading(true);
      try {
        const stats = await fetchProfileStats();
        console.log('Profile stats:', stats);
        
        // Update state with data from server
        setTotalCourses(stats.totalCourses || 0);
        setCompletedCourses(stats.totalCompleted || 0);
        
        // Calculate in-progress courses
        const inProgress = (stats.totalCourses || 0) - (stats.totalCompleted || 0);
        setInProgressCourses(Math.max(0, inProgress));
        
        // Set total lessons completed
        setTotalLessonsCompleted(stats.totalLessonsCompleted || 0);
        
        // Set last activity time to now (or could come from the server if available)
        setLastActivity(new Date());
      } catch (error) {
        console.error("Failed to load profile stats:", error);
        toast({
          title: "Error loading profile data",
          description: "Unable to load your profile statistics.",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    // Fetch data only if authenticated
    if (isAuthenticated && user) {
      loadProfileStats();
    } else if (!isAuthenticated) {
      // Hook handles redirect, set loading false
       setIsLoading(false);
    }
    // Keep dependencies
  }, [isAuthenticated, navigate, user]);

  // Get initials for avatar fallback
  const getInitials = () => {
    if (!user.fullName) return 'U';
    return user.fullName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };
  
  // Format date to readable string
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        
        <main className="flex-1 container py-6">
          <Header 
            title="My Profile" 
            description="View and manage your profile details" 
          />
          
          <div className="grid gap-6 md:grid-cols-3 mt-6">
            {/* Profile Card */}
            <Card className="md:col-span-1">
              <CardHeader className="flex flex-col items-center">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={user?.avatarUrl} alt={user.fullName || 'User'} />
                  <AvatarFallback className="text-lg">{getInitials()}</AvatarFallback>
                </Avatar>
                <CardTitle className="mt-4 text-center text-xl">{user.fullName}</CardTitle>
                <CardDescription className="text-center">{user.email}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Account Type</span>
                  <Badge>{user.role === 'admin' ? 'Admin' : 'Student'}</Badge>
                </div>
                {lastActivity && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Last Activity</span>
                    <span className="text-sm">{formatDate(lastActivity)}</span>
                  </div>
                )}
              </CardContent>
              <CardFooter>
                <Button variant="outline" className="w-full">
                  Edit Profile
                </Button>
              </CardFooter>
            </Card>
            
            {/* Stats and Progress */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Learning Progress</CardTitle>
                <CardDescription>
                  Track your learning journey progress
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                {isLoading ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : (
                  <>
                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                      <div className="flex flex-col items-center justify-center rounded-lg border p-4">
                        <BookOpen className="h-8 w-8 text-primary opacity-75" />
                        <div className="mt-2 text-center">
                          <p className="text-2xl font-bold">{totalCourses}</p>
                          <p className="text-xs text-muted-foreground">Total Courses</p>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-center justify-center rounded-lg border p-4">
                        <Award className="h-8 w-8 text-green-500 opacity-75" />
                        <div className="mt-2 text-center">
                          <p className="text-2xl font-bold">{completedCourses}</p>
                          <p className="text-xs text-muted-foreground">Completed</p>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-center justify-center rounded-lg border p-4">
                        <Clock className="h-8 w-8 text-blue-500 opacity-75" />
                        <div className="mt-2 text-center">
                          <p className="text-2xl font-bold">{inProgressCourses}</p>
                          <p className="text-xs text-muted-foreground">In Progress</p>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-center justify-center rounded-lg border p-4">
                        <Calendar className="h-8 w-8 text-amber-500 opacity-75" />
                        <div className="mt-2 text-center">
                          <p className="text-2xl font-bold">{totalLessonsCompleted}</p>
                          <p className="text-xs text-muted-foreground">Lessons Done</p>
                        </div>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    {/* Overall Progress */}
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium">Overall Progress</span>
                        <span className="text-sm text-muted-foreground">
                          {totalCourses > 0 
                            ? Math.round((completedCourses / totalCourses) * 100)
                            : 0}%
                        </span>
                      </div>
                      <Progress 
                        value={totalCourses > 0 
                          ? Math.round((completedCourses / totalCourses) * 100)
                          : 0} 
                        className="h-2"
                      />
                    </div>
                    
                    {/* Recent Achievements */}
                    <div className="rounded-lg border p-4">
                      <h3 className="mb-2 font-medium">Recent Achievements</h3>
                      {completedCourses > 0 ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Award className="h-5 w-5 text-yellow-500" />
                            <span className="text-sm">Completed your first course</span>
                          </div>
                          {totalLessonsCompleted >= 5 && (
                            <div className="flex items-center gap-2">
                              <Award className="h-5 w-5 text-yellow-500" />
                              <span className="text-sm">Completed 5+ lessons</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          Complete courses to earn achievements
                        </p>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      
      <main className="flex-1 container py-6">
        <Header 
          title="My Profile" 
          description="View and manage your profile details" 
        />
        
        <div className="grid gap-6 md:grid-cols-3 mt-6">
          {/* Profile Card */}
          <Card className="md:col-span-1">
            <CardHeader className="flex flex-col items-center">
              <Avatar className="h-24 w-24">
                <AvatarImage src={user?.avatarUrl} alt={user.fullName || 'User'} />
                <AvatarFallback className="text-lg">{getInitials()}</AvatarFallback>
              </Avatar>
              <CardTitle className="mt-4 text-center text-xl">{user.fullName}</CardTitle>
              <CardDescription className="text-center">{user.email}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Account Type</span>
                <Badge>{user.role === 'admin' ? 'Admin' : 'Student'}</Badge>
              </div>
              {lastActivity && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Last Activity</span>
                  <span className="text-sm">{formatDate(lastActivity)}</span>
                </div>
              )}
            </CardContent>
            <CardFooter>
              <Button variant="outline" className="w-full">
                Edit Profile
              </Button>
            </CardFooter>
          </Card>
          
          {/* Stats and Progress */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Learning Progress</CardTitle>
              <CardDescription>
                Track your learning journey progress
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {isLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <>
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    <div className="flex flex-col items-center justify-center rounded-lg border p-4">
                      <BookOpen className="h-8 w-8 text-primary opacity-75" />
                      <div className="mt-2 text-center">
                        <p className="text-2xl font-bold">{totalCourses}</p>
                        <p className="text-xs text-muted-foreground">Total Courses</p>
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-center justify-center rounded-lg border p-4">
                      <Award className="h-8 w-8 text-green-500 opacity-75" />
                      <div className="mt-2 text-center">
                        <p className="text-2xl font-bold">{completedCourses}</p>
                        <p className="text-xs text-muted-foreground">Completed</p>
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-center justify-center rounded-lg border p-4">
                      <Clock className="h-8 w-8 text-blue-500 opacity-75" />
                      <div className="mt-2 text-center">
                        <p className="text-2xl font-bold">{inProgressCourses}</p>
                        <p className="text-xs text-muted-foreground">In Progress</p>
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-center justify-center rounded-lg border p-4">
                      <Calendar className="h-8 w-8 text-amber-500 opacity-75" />
                      <div className="mt-2 text-center">
                        <p className="text-2xl font-bold">{totalLessonsCompleted}</p>
                        <p className="text-xs text-muted-foreground">Lessons Done</p>
                      </div>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  {/* Overall Progress */}
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Overall Progress</span>
                      <span className="text-sm text-muted-foreground">
                        {totalCourses > 0 
                          ? Math.round((completedCourses / totalCourses) * 100)
                          : 0}%
                      </span>
                    </div>
                    <Progress 
                      value={totalCourses > 0 
                        ? Math.round((completedCourses / totalCourses) * 100)
                        : 0} 
                      className="h-2"
                    />
                  </div>
                  
                  {/* Recent Achievements */}
                  <div className="rounded-lg border p-4">
                    <h3 className="mb-2 font-medium">Recent Achievements</h3>
                    {completedCourses > 0 ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Award className="h-5 w-5 text-yellow-500" />
                          <span className="text-sm">Completed your first course</span>
                        </div>
                        {totalLessonsCompleted >= 5 && (
                          <div className="flex items-center gap-2">
                            <Award className="h-5 w-5 text-yellow-500" />
                            <span className="text-sm">Completed 5+ lessons</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Complete courses to earn achievements
                      </p>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Profile;
